import React, { useState, useEffect } from 'react';
import { roleLabel } from '../../utils/roleLabels';
import { useAuth } from '../../context/AuthContext';
import { useLayout } from '../../context/LayoutContext';
import { Icon } from '../common/Icon';
import { ThemeToggle } from '../common/ThemeToggle';
import {
  Sparkles,
  ChevronDown,
  LogOut,
  User,
  FlaskConical,
  Menu,
  Wrench,
  Sliders
} from 'lucide-react';
import { UserProfileModal } from '../security/UserProfileModal';
import { useSystemBranding, ShieldLogo, BrandTitle } from '../../context/SystemBrandingContext';

interface NavbarProps {
  onOpenWizard: () => void;
  onOpenSync: () => void;
  onOpenProfile?: () => void;
  onOpenExport?: () => void;
  onOpenHelp?: () => void;
  onToggleSidebar?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onOpenWizard, onOpenSync, onOpenProfile, onOpenExport, onOpenHelp, onToggleSidebar }) => {
  const { user, tenant, role, appMode, setAppMode, logout, isSandbox, enterSandbox } = useAuth();
  const {
    categories,
    setActiveCategory,
    setActiveModule,
    getModulesByPlacement,
    setIsConfiguratorOpen
  } = useLayout();
  const { branding } = useSystemBranding();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Kívülre kattintás figyelése a lenyíló menük bezárásához
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.navbar-dropdown-container')) {
        setActiveDropdown(null);
      }
    };
    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  const baseRole = (user?.role || role || 'user') as string;
  const userRole = ['superadmin', 'tenant_admin', 'org_admin'].includes(baseRole)
    ? baseRole
    : ((user?.granular_role || baseRole) as string);
  const isSuperAdmin = userRole === 'superadmin' || userRole === 'tenant_admin';
  const canConfigureMenu = isSuperAdmin || userRole === 'org_admin';

  // Dinamikusan szűrt modulok a 4 dedikált menühely szerint
  const headerDropdownModules = getModulesByPlacement('header_dropdown');
  const directModules = getModulesByPlacement('navbar_direct');
  const toolsModules = getModulesByPlacement('navbar_tools');

  // Akciókezelő a dinamikus gombokhoz és menüpontokhoz
  const handleModuleAction = (modId: string, categoryId?: string) => {
    switch (modId) {
      case 'quick_wizard':
        onOpenWizard();
        break;
      case 'sync_manager':
        onOpenSync();
        break;
      case 'export_tools':
        if (onOpenExport) onOpenExport();
        break;
      case 'help_center':
        if (onOpenHelp) onOpenHelp();
        break;
      case 'public_portal':
        setActiveModule('public_portal');
        break;
      default:
        if (categoryId) setActiveCategory(categoryId);
        setActiveModule(modId);
        break;
    }
  };

  // Dinamikusan felépített kategória-térkép a Felső kategória lenyíló (header_dropdown) modulokhoz
  const dropdownCategoryMap = new Map<string, { id: string; title: string; icon: string; description?: string; modules: typeof headerDropdownModules }>();

  headerDropdownModules.forEach((mod) => {
    const catId = mod.category || 'other';
    const foundCat = categories.find((c) => c.id === catId);
    const catTitle = foundCat?.title || (
      catId === 'transparency_data' ? 'Közzétételi Nyilvántartó' :
      catId === 'platform_mgmt' ? 'Rendszer Admin' :
      catId === 'compliance_reports' ? 'Megfelelőség & Riportok' :
      catId === 'data_exchange' ? 'Adatcsere & Import' : 'Felső Kategória'
    );
    const catIcon = foundCat?.icon || mod.icon || 'Layers';
    const catDesc = foundCat?.description || catTitle;

    if (!dropdownCategoryMap.has(catId)) {
      dropdownCategoryMap.set(catId, {
        id: catId,
        title: catTitle,
        icon: catIcon,
        description: catDesc,
        modules: []
      });
    }
    dropdownCategoryMap.get(catId)!.modules.push(mod);
  });

  const dropdownCategories = Array.from(dropdownCategoryMap.values());

  const getRoleBadgeColor = (r: string) => {
    switch (r) {
      case 'superadmin':
      case 'tenant_admin':
        return 'bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-500/20 dark:text-rose-300 dark:border-rose-500/30';
      case 'org_admin':
        return 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/30';
      case 'editor':
        return 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/30';
      case 'operator':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/30';
      case 'auditor':
        return 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-500/20 dark:text-purple-300 dark:border-purple-500/30';
      default:
        return 'bg-slate-200 text-slate-700 border-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600';
    }
  };

  const getRoleLabel = (r: string) => roleLabel(r);


  return (
    <header className="sticky top-0 z-40 bg-white/95 dark:bg-[#0b0f1a]/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 px-2 sm:px-3 lg:px-4 py-1.5 flex items-center justify-between gap-x-2 text-slate-800 dark:text-slate-100 transition-colors w-full max-w-full shrink-0">
      {/* Brand & Organization Title */}
      <div className="flex items-center gap-1 sm:gap-2.5 shrink-0 min-w-0">
        {onToggleSidebar && (
          <button
            onClick={onToggleSidebar}
            className="p-1.5 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
            title="Oldalsáv összecsukása / kinyitása"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          <ShieldLogo size="md" />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <BrandTitle size="md" />
              {branding.version && (
                <span className="hidden sm:inline text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-mono border border-slate-200 dark:border-slate-700 shrink-0">
                  {branding.version}
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate max-w-[90px] sm:max-w-[130px] md:max-w-[160px] lg:max-w-[200px]">
              {tenant?.name || branding.system_title || 'KözzéTér Pro.'}
            </p>
          </div>
        </div>

        {/* Felső kategória lenyíló menük (v4.5 mellett jobbra) */}
        {dropdownCategories.length > 0 && (
          <div className="hidden sm:flex items-center gap-1 sm:gap-1.5 ml-1 sm:ml-2.5 navbar-dropdown-container">
            {dropdownCategories.map((cat) => {
              const catModules = cat.modules;
              if (!catModules || catModules.length === 0) return null;
              const isOpen = activeDropdown === cat.id;

              return (
                <div key={cat.id} className="relative navbar-dropdown-container">
                  <button
                    onClick={() => setActiveDropdown(isOpen ? null : cat.id)}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1.5 cursor-pointer border ${
                      isOpen
                        ? 'bg-indigo-50 border-indigo-300 text-indigo-700 dark:bg-indigo-950/50 dark:border-indigo-700 dark:text-cyan-300 shadow-xs'
                        : 'bg-slate-100/80 dark:bg-slate-800/60 hover:bg-slate-200/80 dark:hover:bg-slate-700/80 text-slate-700 dark:text-slate-200 border-slate-200/80 dark:border-slate-700/60'
                    }`}
                    title={cat.description || cat.title}
                  >
                    <Icon name={cat.icon} className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400 shrink-0" />
                    <span className="truncate max-w-[120px] lg:max-w-[170px]">{cat.title}</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-slate-200/70 dark:bg-slate-700/70 text-slate-600 dark:text-slate-300 font-mono font-bold">
                      {catModules.length}
                    </span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isOpen && (
                    <div
                      className="absolute top-full left-0 mt-1.5 w-80 sm:w-96 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl p-2.5 z-50 animate-in fade-in zoom-in-95 duration-100"
                    >
                      <div className="px-3 py-2 text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-800/80 mb-1.5 flex items-center justify-between">
                        <span>{cat.title}</span>
                        <span className="font-mono text-[9px] text-slate-400">{catModules.length} modul</span>
                      </div>
                      <div className="space-y-1">
                        {catModules.map((mod) => (
                          <button
                            key={mod.id}
                            onClick={() => {
                              handleModuleAction(mod.id, cat.id);
                              setActiveDropdown(null);
                            }}
                            className="w-full text-left px-3 py-2.5 rounded-xl text-xs text-slate-700 dark:text-slate-200 hover:bg-indigo-50 dark:hover:bg-slate-800/80 hover:text-indigo-600 dark:hover:text-cyan-300 transition flex items-start gap-3 cursor-pointer group"
                          >
                            <Icon name={mod.icon} className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 dark:group-hover:text-cyan-400 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-slate-900 dark:text-slate-100 group-hover:text-indigo-600 dark:group-hover:text-cyan-300 transition-colors">
                                {mod.title}
                              </div>
                              <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug break-words whitespace-normal">
                                {mod.description}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Középső/Jobb szekció: Módváltó, Gyorsgombok, Eszközök & Felhasználó */}
      <div className="flex items-center gap-1 sm:gap-1.5 shrink-0 ml-auto justify-end">
        {/* Kettős Működési Mód Váltó (⚡ Egyszerűsített vs 🛠️ Összetett) */}
        <div className="bg-slate-100 dark:bg-slate-900/90 p-0.5 sm:p-1 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center text-xs shrink-0">
          <button
            onClick={() => setAppMode('simplified')}
            className={`px-2 py-1 sm:py-1.5 rounded-lg font-medium transition flex items-center gap-1 ${
              appMode === 'simplified'
                ? 'bg-emerald-600 text-white dark:bg-emerald-500/20 dark:text-emerald-300 shadow-xs border border-emerald-500/30'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
            }`}
            title="Egyszerűsített nézet laikusoknak 3-lépéses feltöltéssel"
          >
            <Sparkles className="w-3.5 h-3.5 shrink-0" />
            <span className="hidden 3xl:inline">Egyszerűsített</span>
          </button>
          <button
            onClick={() => setAppMode('extended')}
            className={`px-2 py-1 sm:py-1.5 rounded-lg font-medium transition flex items-center gap-1 ${
              appMode === 'extended'
                ? 'bg-blue-600 text-white dark:bg-blue-500/20 dark:text-blue-300 shadow-xs border border-blue-500/30'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
            }`}
            title="Részletes adminisztrátori és auditori felület"
          >
            <Sliders className="w-3.5 h-3.5 shrink-0" />
            <span className="hidden 3xl:inline">Összetett</span>
          </button>
        </div>

        {/* Menü & Jogosultság Vezérlő Gyorsgomb (Adminoknak) */}
        {canConfigureMenu && (
          <button
            onClick={() => setIsConfiguratorOpen(true)}
            className="p-1.5 rounded-xl bg-purple-50 hover:bg-purple-100 dark:bg-purple-950/40 dark:hover:bg-purple-900/60 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-700/60 transition cursor-pointer shrink-0"
            title="Menüpontok helyének és láthatóságának testreszabása (Local)"
          >
            <Sliders className="w-4 h-4" />
          </button>
        )}

        {/* Dinamikus Felső Közvetlen Gombok (navbar_direct) */}
        {directModules.map((mod) => {
          const isWizard = mod.id === 'quick_wizard';
          return (
            <button
              key={mod.id}
              onClick={() => handleModuleAction(mod.id, mod.category)}
              className={`flex items-center gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-lg font-medium text-xs shadow-xs transition cursor-pointer shrink-0 ${
                isWizard
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  : 'bg-indigo-50 dark:bg-indigo-500/10 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 text-indigo-700 dark:text-cyan-300 border border-indigo-200 dark:border-indigo-500/30'
              }`}
              title={mod.description}
            >
              <Icon name={mod.icon} className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden 3xl:inline">{mod.title}</span>
              <span className="hidden 2xl:inline 3xl:hidden">{isWizard ? 'Varázsló' : mod.title}</span>
            </button>
          );
        })}

        {/* Homokozó (DEMO) Mód Jelvény - ha aktív */}
        {(isSandbox || tenant?.is_demo) && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-400 text-xs font-bold shadow-xs shrink-0">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
            <span className="hidden sm:inline">DEMO</span>
          </div>
        )}

        {/* ── ESZKÖZÖK & GYORSFUNKCIÓK LEFOGÓ DROPDOWN (navbar_tools) ── */}
        {(toolsModules.length > 0 || !isSandbox || canConfigureMenu) && (
          <div className="relative navbar-dropdown-container">
            <button
              onClick={() => setActiveDropdown(activeDropdown === 'tools' ? null : 'tools')}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1.5 cursor-pointer border ${
                activeDropdown === 'tools'
                  ? 'bg-indigo-50 border-indigo-300 text-indigo-700 dark:bg-indigo-950/50 dark:border-indigo-700 dark:text-cyan-300'
                  : 'bg-slate-100 dark:bg-slate-800/80 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700'
              }`}
              title="Rendszereszközök, szinkronizáció, export és súgó"
            >
              <Wrench className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400 shrink-0" />
              <span className="font-semibold hidden sm:inline">Eszközök</span>
              <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${activeDropdown === 'tools' ? 'rotate-180' : ''}`} />
            </button>

            {activeDropdown === 'tools' && (
              <div
                className="absolute top-full right-0 mt-1.5 w-72 sm:w-80 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl p-2 z-50 animate-in fade-in zoom-in-95 duration-100"
              >
                <div className="px-3 py-1.5 text-[10px] uppercase font-bold tracking-wider text-slate-400 dark:text-slate-500 border-b border-slate-100 dark:border-slate-800 mb-1 flex items-center justify-between">
                  <span>Gyorsműveletek & Eszközök</span>
                  <span className="text-[9px] font-mono text-indigo-600 dark:text-cyan-400">KözzéTér Pro</span>
                </div>

                <div className="space-y-0.5">
                  {/* Dinamikusan ide rendelt modulok */}
                  {toolsModules.map((mod) => (
                    <button
                      key={mod.id}
                      onClick={() => {
                        handleModuleAction(mod.id, mod.category);
                        setActiveDropdown(null);
                      }}
                      className="w-full text-left px-2.5 py-2 rounded-xl text-xs hover:bg-indigo-50 dark:hover:bg-slate-800/80 hover:text-indigo-600 dark:hover:text-cyan-300 transition flex items-center gap-2.5 cursor-pointer group"
                      title={mod.description}
                    >
                      <div className="p-1.5 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-cyan-400 group-hover:bg-indigo-500 group-hover:text-white transition">
                        <Icon name={mod.icon} className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 dark:text-slate-200 group-hover:text-indigo-600 dark:group-hover:text-cyan-300">
                          {mod.title}
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                          {mod.description}
                        </div>
                      </div>
                    </button>
                  ))}

                  {/* Homokozó Mód indítása (ha nem aktív még) */}
                  {!isSandbox && !tenant?.is_demo && (
                    <button
                      onClick={() => {
                        enterSandbox();
                        setActiveDropdown(null);
                      }}
                      className="w-full text-left px-2.5 py-2 rounded-xl text-xs hover:bg-amber-50 dark:hover:bg-slate-800/80 hover:text-amber-700 dark:hover:text-amber-300 transition flex items-center gap-2.5 cursor-pointer group"
                    >
                      <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 group-hover:bg-amber-500 group-hover:text-white transition">
                        <FlaskConical className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 dark:text-slate-200 group-hover:text-amber-700 dark:group-hover:text-amber-300">
                          Homokozó (DEMO) Mód
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                          Elkülönített tesztadatok szerkesztése
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Menü & Jogosultság Vezérlő Modal Megnyitása */}
                  {canConfigureMenu && (
                    <div className="pt-1 mt-1 border-t border-slate-100 dark:border-slate-800">
                      <button
                        onClick={() => {
                          setIsConfiguratorOpen(true);
                          setActiveDropdown(null);
                        }}
                        className="w-full text-left px-2.5 py-2 rounded-xl text-xs hover:bg-purple-50 dark:hover:bg-purple-950/40 hover:text-purple-700 dark:hover:text-purple-300 transition flex items-center gap-2.5 cursor-pointer group border border-dashed border-purple-300/80 dark:border-purple-700/60 bg-purple-50/40 dark:bg-purple-950/20"
                      >
                        <div className="p-1.5 rounded-lg bg-purple-500/20 text-purple-600 dark:text-purple-400 group-hover:bg-purple-600 group-hover:text-white transition">
                          <Sliders className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-purple-900 dark:text-purple-200 flex items-center gap-1.5">
                            <span>Menü- & Nézetvezérlő</span>
                            <span className="text-[9px] px-1.5 py-0.2 bg-purple-200 dark:bg-purple-800/60 text-purple-800 dark:text-purple-200 rounded font-mono font-bold">
                              LOCAL
                            </span>
                          </div>
                          <div className="text-[10px] text-purple-700/80 dark:text-purple-300/80 truncate">
                            4 menühely, szerepkörök, nézet szabályozása
                          </div>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}


        {/* Sötét / Világos Téma Váltó Gomb */}
        <ThemeToggle />

        {/* Felhasználói Profil & Kilépés */}
        {user ? (
          <div className="flex items-center gap-1.5 sm:gap-2 pl-1.5 sm:pl-2 border-l border-slate-200 dark:border-slate-800">
            <button
              onClick={() => {
                if (onOpenProfile) onOpenProfile();
                else setShowProfileModal(true);
              }}
              className="flex items-center gap-1.5 sm:gap-2 p-1 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer text-right group"
              title="Profil és biztonsági beállítások (2FA, jelszó, e-mail)"
            >
              <div className="hidden 2xl:block">
                <div className="text-xs font-semibold text-slate-900 dark:text-slate-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition truncate max-w-[85px] lg:max-w-[100px] xl:max-w-[120px]">
                  {user.name}
                </div>
                <div className="flex items-center justify-end gap-1 mt-0.5">
                  <span className={`inline-block px-1.5 py-0.2 rounded border text-[9px] font-mono shrink-0 ${getRoleBadgeColor(role)}`}>
                    {getRoleLabel(role)}
                  </span>
                  {tenant?.name && (
                    <span className="hidden 3xl:inline text-[10px] text-slate-500 dark:text-slate-400 truncate max-w-[100px] font-medium" title={tenant.name}>
                      • {tenant.name}
                    </span>
                  )}
                </div>
              </div>
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 group-hover:bg-indigo-600 group-hover:text-white transition flex items-center justify-center font-bold text-xs border border-indigo-500/20 shrink-0">
                <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </div>
            </button>

            <button
              onClick={() => logout()}
              className="p-1.5 text-slate-500 dark:text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition cursor-pointer shrink-0"
              title="Kijelentkezés"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="text-xs text-slate-400">Vendég</div>
        )}

        {/* Fallback ha nincs onOpenProfile megadva */}
        {!onOpenProfile && (
          <UserProfileModal
            isOpen={showProfileModal}
            onClose={() => setShowProfileModal(false)}
          />
        )}
      </div>
    </header>
  );
};
